/**
 * ALERON header.
 *
 * Two jobs:
 *
 * 1. The transparent-over-hero state. On the homepage the header sits clear
 *    over the first slide with a large wordmark and white type, then turns
 *    solid white with black type and a smaller wordmark once the hero has
 *    scrolled past. Every other template starts solid, which happens for free
 *    because those pages render no hero element to observe.
 *
 * 2. Keeping the bag count in sync with the cart without a page load.
 *
 * The scroll state is driven by an IntersectionObserver on the hero rather
 * than a scroll listener, so nothing runs on the main thread while scrolling.
 */

import { AleronOverlay, define } from '@theme/aleron-overlay';
import { StandardEvents } from '@shopify/events';

class AleronHeader extends HTMLElement {
  #observer = null;

  connectedCallback() {
    this.#watchHero();
    this.#watchCart();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /* ---------------------------------------------------------------------
     Transparent -> solid
     --------------------------------------------------------------------- */

  #watchHero() {
    // Absent on every template that should start solid.
    const hero = document.querySelector('[data-aleron-hero]');

    if (!this.hasAttribute('data-transparent') || !hero) {
      this.setAttribute('data-solid', '');
      return;
    }

    if (!('IntersectionObserver' in window)) {
      // Without the observer the safe state is solid: black on white always
      // has contrast, white on an unknown image does not.
      this.setAttribute('data-solid', '');
      return;
    }

    // The flip point is a CONSTANT: the solid bar's height. It must never be
    // read from this.offsetHeight. The header is a different height in each
    // state, so a threshold taken from its current height moves every time
    // the state changes: going solid shrank the header, which pulled the
    // threshold back past the hero's edge, which flipped it transparent,
    // which grew it again. On a phone that looped with the page standing
    // still — 380 flips measured across one slow scroll, 180 of them with no
    // input at all. The hero's position depends only on scroll, so a fixed
    // threshold gives exactly one flip each way.
    const threshold = this.#solidBarHeight();

    this.#observer = new IntersectionObserver(
      ([entry]) => {
        // Once the hero's bottom edge passes under the solid bar, go solid.
        this.toggleAttribute('data-solid', !entry.isIntersecting);
      },
      { rootMargin: `-${threshold}px 0px 0px 0px`, threshold: 0 }
    );
    this.#observer.observe(hero);
  }

  /**
   * The solid bar's height in px, read from the design token so CSS stays
   * the single source of truth for it.
   */
  #solidBarHeight() {
    const raw = getComputedStyle(this).getPropertyValue('--aleron-header-height').trim();
    const value = parseFloat(raw);
    if (!Number.isFinite(value)) return 56;
    if (raw.endsWith('rem')) {
      return value * parseFloat(getComputedStyle(document.documentElement).fontSize);
    }
    return value;
  }

  /* ---------------------------------------------------------------------
     Bag count
     --------------------------------------------------------------------- */

  #watchCart() {
    // Same event Horizon's own cart icon listens to, so the count stays in
    // step with the drawer no matter which component triggered the change.
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
    this.#restoreCount();
  }

  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        this.setCount(cart?.totalQuantity ?? detail?.itemCount ?? 0);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[aleron-header] cart update failed:', error);
      });
  };

  #handlePageShow = (event) => {
    // Restoring from bfcache can show a stale count.
    if (event.persisted) this.#restoreCount();
  };

  /** @param {number} count */
  setCount(count) {
    for (const node of this.querySelectorAll('[data-aleron-cart-count]')) {
      node.textContent = String(count);
    }

    // The label reads "BAG (2)" in text mode and is the icon's accessible
    // name in icon mode; both need the live count.
    for (const node of this.querySelectorAll('[data-aleron-cart-label]')) {
      node.textContent = node.dataset.aleronCartLabel.replace('__COUNT__', String(count));
    }

    this.toggleAttribute('data-has-items', count > 0);

    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
    } catch {
      // Private mode — the count is still correct for this page view.
    }
  }

  #restoreCount() {
    let stored;
    try {
      stored = sessionStorage.getItem('cart-count');
    } catch {
      return;
    }
    if (!stored) return;

    try {
      const { value, timestamp } = JSON.parse(stored);
      // Only trust a very recent value; otherwise the server-rendered count wins.
      if (Date.now() - timestamp > 10000) return;

      const count = parseInt(value, 10);
      const current = this.querySelector('[data-aleron-cart-count]')?.textContent?.trim();
      if (Number.isFinite(count) && String(count) !== current) this.setCount(count);
    } catch {
      // Malformed entry — ignore it.
    }
  }
}

/**
 * The full-screen navigation overlay. Collections are listed in large
 * uppercase type, driven by the menu chosen in Shopify navigation.
 */
class AleronMenuOverlay extends AleronOverlay {
  onOpen() {
    // Stagger the links in. Index is set here rather than in Liquid so the
    // delay stays correct if the menu is edited in the theme editor.
    const items = this.querySelectorAll('[data-aleron-menu-item]');
    items.forEach((item, i) => item.style.setProperty('--aleron-stagger', `${i * 40}ms`));
  }
}

define('aleron-header', AleronHeader);
define('aleron-menu-overlay', AleronMenuOverlay);
